'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import {
  simulatedSubscription,
  type TourSubscription,
} from '@/features/onboarding/lib/welcome-plan'
import { usePlans, useSubscription } from '@/service/billing.hook'
import { debugModeAtom } from '@/state/debug'
import { tourPlanPreviewAtom } from '@/state/onboarding'

/** Never set on prod: the dock that sets it doesn't render there, and a stale value is ignored. */
const PREVIEW_ALLOWED = process.env.NEXT_PUBLIC_ENV !== 'prod'

/**
 * The subscription the QA dock is playing as, or undefined when it isn't (or the catalog
 * hasn't loaded). Only while debug mode (⌘⇧\) is on — the dock lives there, so the app never
 * keeps playing another plan with the dock out of sight. Only fetches the catalog then.
 */
export function useSimulatedSubscription(): TourSubscription | undefined {
  const preview = useAtomValue(tourPlanPreviewAtom)
  const debugMode = useAtomValue(debugModeAtom)
  const active = PREVIEW_ALLOWED && debugMode && preview !== null
  const { data } = usePlans({ enabled: active })
  return useMemo(
    () => (active && preview && data ? simulatedSubscription(data.plans, preview) : undefined),
    [active, preview, data]
  )
}

/** The subscription the welcome tour tours: the QA dock's, else the user's own. */
export function useTourSubscription(): TourSubscription | undefined {
  const simulated = useSimulatedSubscription()
  const { data } = useSubscription()
  return simulated ?? data
}
